<template>
  <hypto-popup :title="'Upload File'" :footer="false">
    <template v-slot:body>
      <div class="merge-fields" >
        <md-field id="TxnTypes" :class="{'input-error': $v.txnType.$error}">
          <label>Transaction Types</label>
          <md-select v-model="txnType" @md-opened="setDropdownPosOffset('TxnTypes')">
            <md-option v-for="(txn, index) in txnOptions" :key="index" :value="txn.value">
              {{txn.text}}
            </md-option>
          </md-select>
        </md-field>
        <h6 class="error-message" v-if="$v.txnType.$error">Please select a transaction type</h6>

        <div class="note" v-if="txnType">
          <b>Note:</b>
          <p>
            1.
            <span class="sample" @click="downloadSample">
              <u>Download sample file</u>
              <i class="fa fa-file-download" />
            </span>
          </p>
          <p>2. Account numbers must be prefixed with '#'</p>
          <p>3. Only csv files are supported</p>
          <p>4. File size must be maximum 1MB</p>
          <p>5. File name should contain upto 200 chars &amp; must start with alphanum chars only and can contain alphanum, hyphen, underscore and dot</p>
        </div>

        <div class="file-name" v-if="file">
          <i class="fa fa-file-excel" />
          <span>{{file.name}}</span>
          <i class="fa fa-times" v-if="formErrors.import.length || errorCsv" />
          <i class="fa fa-check" v-else-if="csvDetails.processed" />
        </div>
        <div id="CsvProgress" />

        <div
          v-if="txnType"
          class="browse"
          @click="$refs.Upload.click()"
          @drop.prevent="addFile"
          @dragover.prevent
        >
          <input type="file" style="display: none" @input="getFile"
                :ref="'Upload'" accept=".csv" />
          <i class="fa fa-cloud-upload-alt" />
          <p>Drag and Drop (.csv)</p>
          <span>or</span>
          <p>Browse</p>
        </div>
        <h6 class="error-message" v-if="$v.file.$error">Please select a file</h6>
        <error-info :errorList="formErrors.import" />
        <h6 class="error-message" v-if="errorCsv">
          {{errorCsv.msg}}
          <a
            v-if="errorCsv.file"
            :href="errorCsv.file"
            :download="file.name.slice(0, -4) + '-output.csv'"
            class="link"
          >
            {{file.name.slice(0, -4)}}-output.csv
          </a>
        </h6>

        <button class="primary-btn" @click="confirmUpload">
          Next
        </button>
      </div>
    </template>
  </hypto-popup>
</template>

<script>
import ErrorInfo from '@/components/global/ErrorInfo'
import HyptoPopup from '@/components/global/HyptoPopup'

import ApiServices from '@/services/api/imports'
import { openPopup, setDropdownPosOffset, checkFormErrors, amountFormat } from '@/utils/helpers'
import { validIfsc, validVpa, accountNumber, refNum, alphaNumericSpace } from '@/utils/helpers/validators'
import { required } from 'vuelidate/lib/validators'
import { mapMutations } from 'vuex'
import Papa from 'papaparse'
import NProgress from 'nprogress'

export default {
  name: 'ImportUpload',
  components: { ErrorInfo, HyptoPopup },
  data () {
    return {
      file: null,
      txnType: null,
      csvDetails: {},
      errorCsv: null,
      formErrors: {
        import: []
      },
      browserSupport: true
    }
  },
  props: {
    txnOptions: Array
  },
  validations: {
    file: {
      required
    },
    txnType: {
      required
    }
  },
  watch: {
    file () {
      this.formErrors.import = []
      this.errorCsv = null
      this.csvDetails = {}
    },
    txnType () {
      this.formErrors.import = []
      this.errorCsv = null
      this.csvDetails = {}
      this.file = null
    }
  },
  methods: {
    setDropdownPosOffset,
    checkFormErrors,
    openPopup,
    amountFormat,
    ...mapMutations(['SET_SECTION_DETAILS']),
    validateFile (file) {
      if (file.size > 1000000) {
        this.errorToastMsg('Please upload a file of size upto 1MB')
        return false
      } else if (!/^[a-zA-Z0-9]{1}[a-zA-Z0-9.\-_]{0,195}\.csv$/.test(file.name)) {
        this.errorToastMsg('Invalid File name. Please check the instructions')
        return false
      }
      return true
    },
    confirmUpload () {
      this.$v.$touch()
      if (!this.$v.$error && !this.checkFormErrors(this.formErrors) && (this.csvDetails.processed || !this.browserSupport)) {
        this.openPopup('ImportConfirmation')
        this.SET_SECTION_DETAILS({
          ...this.csvDetails,
          txn_type: this.txnType,
          file: this.file,
          csv_validated: this.browserSupport
        })
      }
    },
    addFile (e) {
      this.file = null
      const droppedFiles = [...e.dataTransfer.files]
      const csvFiles = droppedFiles.filter(item => item.type === 'text/csv')

      if (droppedFiles.length > 1) {
        this.errorToastMsg('Please upload only 1 .csv file')
      } else if (csvFiles.length === 0) {
        this.errorToastMsg('Please upload a .csv file')
      } else if (this.validateFile(csvFiles[0])) {
        this.file = csvFiles[0]
        this.parseCSV()
      }
    },
    getFile () {
      const file = this.$refs.Upload.files[0]
      if (file) {
        this.file = null
        if (this.validateFile(file)) {
          this.file = file
          this.parseCSV()
        }
      }
    },
    parseCSV () {
      const validate = this.validateCsv()
      let headers = []
      if (this.txnType === 'BANK_TRANSFERS') {
        validate.process = validate.processBT
        headers = ['amount', 'payment_type', 'number', 'ifsc', 'upi_id', 'beneficiary_name', 'reference_number', 'note']
      } else {
        validate.process = validate.processBV
        headers = ['number', 'ifsc', 'reference_number']
      }

      let errorMsg = null
      let totalRows = 0

      NProgress.configure({ parent: '#CsvProgress' })
      NProgress.start()

      const timer = setTimeout(() => {
        if (totalRows === 0) {
          this.browserSupport = false
          NProgress.done()
        }
      }, 2000)

      Papa.parse(this.file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: title => {
          title = title.trim()
          let headIndex = headers.indexOf(title)
          if (headIndex > -1) {
            headers.splice(headIndex, 1)
          }
          return title
        },
        step: (result, parser) => {
          if (totalRows === 0) {
            if (headers.length) {
              errorMsg = `The following headers are missing in csv file - ${headers.join(', ')}`
              parser.abort()
            }
          }

          if (!result.meta.aborted) {
            if (totalRows >= 500) {
              errorMsg = 'Maximum of 500 transactions can be initiated. Please update the csv file.'
              parser.abort()
            }

            const record = result.data
            validate.populate(record)
            validate.process()
            validate.rowCheckout()
            totalRows++
          }
        },
        complete: () => {
          clearTimeout(timer)
          if (validate.errorOutput(totalRows) || errorMsg) {
            if (errorMsg) {
              this.formErrors['import'] = [errorMsg]
            }
            NProgress.done()
          } else {
            this.fetchBalance(totalRows, validate.getAmt())
          }
        },
        error: err => {
          clearTimeout(timer)
          this.formErrors['import'] = ['Unable to process file ' + err]
          NProgress.done()
        }
      })
    },
    validateCsv () {
      let outputCsv = []
      let totalAmt = 0
      let errorReason = []
      let track = {
        refNums: [],
        beneAcc: [],
        upiIds: []
      }
      let invalidEntries = 0
      let data = {}

      const self = {
        populate: record => {
          const returnVal = val => {
            if (val) {
              return val.trim()
            }
            return val
          }

          for (let entry in record) {
            const val = entry === 'amount' ? record[entry].split(',').join('') : record[entry]
            data[entry] = returnVal(val)
          }
        },
        processBT: () => {
          self.duplicates()
          self.amount(data.amount)
          self.paymentType(data.payment_type)
          self.beneName(data.beneficiary_name)
          self.note(data.note)
        },
        processBV: () => {
          /* eslint-disable camelcase */
          const { number, ifsc, reference_number } = data
          self.accNumber(number)
          self.ifsc(ifsc)
          if (reference_number) {
            self.refNum(reference_number)
          }
        },
        duplicates: () => {
          /* eslint-disable camelcase */
          let { upi_id, number, ifsc, payment_type, reference_number } = data
          if (upi_id && payment_type === 'UPI') {
            if (number || ifsc) {
              errorReason.push('Provide either upi_id or account number & ifsc for UPI transfer')
            } else {
              self.upiId(upi_id)
            }
          } else {
            self.accNumber(number)
            self.ifsc(ifsc)
          }
          if (reference_number) {
            self.refNum(reference_number)
          }
        },
        amount: val => {
          if (!(/^[0-9]+(\.[0-9][0-9]?)?$/).test(val) || val <= 0) {
            errorReason.push('Amount should be in numeric format and greater than 0')
          } else {
            val = parseFloat(val)
            totalAmt += val
          }
        },
        paymentType: val => {
          let amount = data.amount || 0
          if (isNaN(amount)) {
            amount = 0
          }
          switch (val) {
            case 'UPI':
              if (amount > 100000) {
                errorReason.push('Amount should be maximum of Rs.1 lakh for UPI transfers')
              }
              break
            case 'IMPS':
              if (amount > 500000) {
                errorReason.push('Amount should be maximum of Rs.5 lakh for IMPS transfers')
              }
              break
            case 'RTGS':
              if (amount < 200000) {
                errorReason.push('Amount should be minimum of Rs.2 lakh for RTGS transfers')
              }
              break
            case 'NEFT':
              break
            default: errorReason.push('Invalid Payment Type')
          }
        },
        accNumber: val => {
          if (!val) {
            errorReason.push('Account Number should contain 1 to 34 alphanumeric characters')
            return
          }
          if (!val.startsWith('#')) {
            errorReason.push(`Account numbers should be prefixed with '#' character`)
            return
          }
          val = val.slice(1)
          const beneAcc = `${val}-${data.ifsc}`
          if (track.beneAcc.includes(beneAcc)) {
            errorReason.push('You have provided duplicate beneficiary account details')
          } else {
            track.beneAcc.push(beneAcc)
          }
          if (!val || !accountNumber(val.slice(1))) {
            errorReason.push('Account Number should contain 1 to 34 alphanumeric characters')
          }
        },
        ifsc: val => {
          if (!val || !validIfsc(val)) {
            errorReason.push('Invalid IFSC')
          }
        },
        upiId: val => {
          if (track.upiIds.includes(val)) {
            errorReason.push('You have provided duplicate beneficiary account details')
          } else {
            track.upiIds.push(val)
          }
          if (!validVpa(val)) {
            errorReason.push('Invalid UPI ID. Please verify')
          }
        },
        refNum: val => {
          if (track.refNums.includes(val)) {
            errorReason.push('You have provided duplicate reference numbers')
          } else {
            track.refNums.push(val)
          }
          if (!refNum(val)) {
            errorReason.push('Reference Number should contain only alphanumeric characters, hyphen, underscore or dot')
          }
        },
        note: val => {
          if (!alphaNumericSpace(val)) {
            errorReason.push('Note should contain only space or alphanumeric characters')
          }
          if (val.length < 2) {
            errorReason.push('Note should contain atleast 2 characters')
          }
        },
        beneName: val => {
          val = val.trim()
          if (val.length === 0) {
            errorReason.push('Beneficiary Name should not be blank')
          } else if (!alphaNumericSpace(val)) {
            errorReason.push('Beneficiary Name should contain only alphanumeric and space characters')
          } else if (val.length > 35) {
            errorReason.push('Beneficiary Name should contain maximum of 35 characters')
          }
        },
        rowCheckout: () => {
          outputCsv.push({
            ...data,
            errors: errorReason.join(', ')
          })

          if (errorReason.length) {
            invalidEntries++
            errorReason = []
          }
          data = {}
        },
        errorOutput: totalRows => {
          if (totalRows === 0) {
            this.errorCsv = {
              msg: `There are no records in the csv file. Please verify the csv file.`
            }
            return true
          }

          if (invalidEntries) {
            const csv = Papa.unparse(outputCsv)
            let csvData = new Blob([csv], {
              type: 'text/csv;charset=utf-8;'
            })
            this.errorCsv = {
              msg: `There are ${invalidEntries}/${totalRows} invalid entries. Please check`,
              file: window.URL.createObjectURL(csvData)
            }
            return true
          }
          return false
        },
        getAmt: () => {
          return totalAmt
        }
      }
      return self
    },
    fetchBalance (rows, amount) {
      ApiServices.fetchCharges({txn_type: this.txnType}).then(response => {
        let balance = null
        if (response.success) {
          let data = response.data
          balance = data.balance
          if (data.txn_cost) {
            amount = rows * data.txn_cost
          }
          if (balance < amount) {
            this.formErrors['import'] = [`Account balance ${this.amountFormat(balance)} is insufficient to process fund transfers`, `Total payouts amount is ${this.amountFormat(amount)} (excl. charges & GST)`]
          }
        }

        this.csvDetails = {
          processed: true,
          rows,
          amount,
          balance,
          show_balance: response.data.hasOwnProperty('balance')
        }
        NProgress.done()
      })
    },
    downloadSample () {
      let file = {}
      if (this.txnType === 'BANK_TRANSFERS') {
        file['csv'] = [
          'amount,payment_type,number,ifsc,upi_id,beneficiary_name,reference_number,note\n100,IMPS,#87678,HDFC0000050,,tester1,,test1\n200,NEFT,#98078575,ICIC0000050,,tester2,refnum1234,test2\n3000,UPI,,,5476577@paytm,tester3,,test3'
        ]
        file['name'] = 'BankTransferSample.csv'
      } else {
        file['csv'] = [
          'number,ifsc,reference_number\n#87678,HDFC0000050\n#98078575,ICIC0000080,refnum1234\n#1258575,ICIC0000080,refnum5678'
        ]
        file['name'] = 'BankVerificationSample.csv'
      }

      let blob = new Blob(file.csv, {
        type: 'text/csv'
      })
      let link = document.createElement('a')
      link.setAttribute('href', URL.createObjectURL(blob))
      link.setAttribute('download', file.name)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/table.scss';

  .file-name {
    @include custom-flex(null);
    color: $grey-text;
    margin: 20px 0 10px;

    span {
      display: block;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      max-width: 80%;
      margin: 0 15px 0 7px;
    }
    .fa-check {
      color: $increase;
    }
    .fa-times {
      color: $error;
    }
  }

  .sample {
    color: $primary;
    cursor: pointer;

    i {
      margin-left: 5px;
    }
  }
  .note {
    margin-top: 10px;
    color: $grey-light;
    text-align: left;
    font-style: italic;
  }
  .link {
    margin-left: 3px;
    word-break: break-all;
    text-decoration: underline !important;
    color: $primary !important;
  }

  .browse {
    margin: 12px 0;
    border: 2px dashed $primary;
    text-align: center;
    padding: 15px;
    border-radius: 5px;
    cursor: pointer;

    i {
      display: block;
      color: $primary;
    }

    p {
      color: $primary;
      display: block;
      margin: 3px auto;
    }
  }

  .primary-btn {
    margin-top: 10px;
  }

  #CsvProgress {
    position: relative;
    overflow: hidden;
    width: 300px;
  }
</style>

<style lang="scss">
  @import '@/assets/scss/mixins.scss';

  #CsvProgress {
    #nprogress {
      height: 8px;
      border: 2px solid $grey-outline;

      .bar {
        background: $primary;
        position: absolute;
        top: 2px;
        left: 2px;
        width: 100%;
        height: 4px;
      }
    }
  }
</style>
